import { ForwardedRef, forwardRef, useContext, useEffect } from 'react';
import { faSquare } from '@fortawesome/pro-regular-svg-icons';
import { faSquareCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SelectContext } from 'components/select/SelectContext';
import * as Styled from 'components/select/select-list-item/styled';
import { SelectItem, SelectListItemProps } from 'components/select/types';
import { selectPredicate } from 'components/select/util';
import styled from 'styled-components';

const SelectListItemPrimitive = forwardRef(
    <T, Item extends SelectItem<T>>(
        props: SelectListItemProps<T, Item>,
        ref: ForwardedRef<HTMLLIElement>,
    ) => {
        const {
            value,
            checkable = false,
            children,
            className = '',
            disabled,
            ...restSelectProps
        } = props;
        const {
            items,
            appendItem,
            selectedItem,
            selectedItems,
            highlightedIndex,
            searchItems,
            getProps,
        } = useContext(SelectContext);
        const curriedPredicate = selectPredicate(value);
        const isAppended = items.findIndex(curriedPredicate) !== -1;
        const searchItemsOrDefault = searchItems && searchItems.length > 0 ? searchItems : items;
        const index = searchItemsOrDefault.findIndex(curriedPredicate);
        const itemsSelected = Array.isArray(selectedItems) ? selectedItems : [];
        const isSelected = value === selectedItem || itemsSelected.some(curriedPredicate);

        useEffect(() => {
            if (!isAppended) {
                appendItem(value);
            }
        }, [appendItem, value, isAppended]);

        if (index === -1) {
            return null;
        }

        if (searchItems && !searchItems?.some(curriedPredicate)) {
            return null;
        }

        return (
            <Styled.SelectListItem
                isSelected={isSelected}
                isHighlighted={highlightedIndex === index}
                checkable={checkable}
                disabled={disabled}
                aria-disabled={disabled}
                className={className}
                {...getProps({
                    ref,
                    item: value,
                    index,
                    ...restSelectProps,
                })}
            >
                {checkable && (
                    <SelectListItemIndicator isSelected={isSelected}>
                        {isSelected ? (
                            <FontAwesomeIcon icon={faSquareCheck} />
                        ) : (
                            <FontAwesomeIcon icon={faSquare} />
                        )}
                    </SelectListItemIndicator>
                )}
                {children}
            </Styled.SelectListItem>
        );
    },
);

const SelectListItem = styled(SelectListItemPrimitive)``;
const SelectListItemIndicator = Styled.CheckIconWrapper;

export { SelectListItem, SelectListItemIndicator };
